import { useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';

/**
 * Cycling "Thinking…" label shown while waiting on the model.
 *
 * Replaces a bare spinner: a changing word reads as progress even when nothing
 * has streamed yet, which matters because the first token can take a while when
 * the agent is running tools.
 */

export const THINKING_LABELS = [
  'Thinking',
  'Pondering',
  'Interpolating',
  'Ruminating',
  'Deliberating',
  'Considering',
  'Percolating',
  'Noodling',
  'Cogitating',
  'Mulling',
  'Computing',
  'Reticulating',
  'Untangling',
  'Synthesizing',
  'Correlating',
  'Extrapolating',
  'Triangulating',
  'Contemplating',
  'Wrangling',
  'Marinating',
] as const;

/** How long each label is held, in ms. */
export const LABEL_DURATION_MS = 2800;

/**
 * Shuffled label order with no adjacent repeats across shuffles.
 *
 * A plain `Math.random()` pick per tick repeats words often enough to look
 * broken; walking a shuffled deck instead guarantees variety, and seeding the
 * next deck so it can't start with the word that just showed avoids the one
 * visible artifact of that approach.
 */
export const shuffleLabels = (
  labels: readonly string[],
  avoidFirst?: string,
  random: () => number = Math.random,
): string[] => {
  const deck = [...labels];
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  if (avoidFirst !== undefined && deck.length > 1 && deck[0] === avoidFirst) {
    [deck[0], deck[1]] = [deck[1], deck[0]];
  }
  return deck;
};

const usePrefersReducedMotion = (): boolean => {
  const [prefersReduced, setPrefersReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setPrefersReduced(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return prefersReduced;
};

interface ThinkingIndicatorProps {
  /** Override the label list — mainly for tests and stories. */
  labels?: readonly string[];
  className?: string;
}

export const ThinkingIndicator: FC<ThinkingIndicatorProps> = ({
  labels = THINKING_LABELS,
  className = '',
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const deckRef = useRef<string[]>([]);
  const positionRef = useRef(0);

  const initialLabel = useMemo(() => {
    deckRef.current = shuffleLabels(labels);
    positionRef.current = 0;
    return deckRef.current[0] ?? 'Thinking';
  }, [labels]);

  const [label, setLabel] = useState(initialLabel);

  useEffect(() => {
    // Reduced motion holds one word: cycling text is itself motion, and the
    // shimmer is doing the "still working" job for everyone else.
    if (prefersReducedMotion) return;
    if (labels.length < 2) return;

    const interval = setInterval(() => {
      positionRef.current += 1;
      if (positionRef.current >= deckRef.current.length) {
        deckRef.current = shuffleLabels(
          labels,
          deckRef.current[deckRef.current.length - 1],
        );
        positionRef.current = 0;
      }
      setLabel(deckRef.current[positionRef.current]);
    }, LABEL_DURATION_MS);

    return () => clearInterval(interval);
  }, [labels, prefersReducedMotion]);

  return (
    <div
      className={`flex items-center gap-2 ${className}`}
      // The label changes every couple of seconds; announcing each one would
      // spam a screen reader. Announce the state once via the sr-only text
      // below and keep the decorative cycling text out of the a11y tree.
      role="status"
    >
      <span className="sr-only">Generating a response</span>
      <span
        aria-hidden="true"
        className={
          prefersReducedMotion
            ? 'text-sm text-muted-foreground'
            : 'text-sm text-transparent bg-clip-text bg-[length:200%_100%] animate-thinking-shimmer bg-gradient-to-r from-muted-foreground via-foreground to-muted-foreground'
        }
      >
        {label}…
      </span>
    </div>
  );
};
